<template>
	<div class="option-picker">
		<div class="option-picker__header">
			<div
				v-if="stepLabel"
				class="option-picker__step"
			>
				{{ stepLabel }}
			</div>
			<h1 class="option-picker__title sgnl-h6-medium sgnl-text-color-default">{{ title }}</h1>
			<p
				v-if="subtitle"
				class="option-picker__subtitle sgnl-paragraph-s-regular sgnl-text-color-secondary"
			>
				{{ subtitle }}
			</p>
		</div>

		<div
			class="option-picker__grid"
			:style="`--option-columns: ${columns}`"
		>
			<button
				v-for="option in options"
				:key="option.id"
				type="button"
				class="option"
				:class="{ 'option--selected': modelValue === option.id }"
				:aria-pressed="modelValue === option.id"
				@click="$emit('update:modelValue', option.id)"
			>
				<span class="option__icon">
					<component :is="option.icon" />
				</span>
				<span class="option__label sgnl-paragraph-s-medium sgnl-text-color-default">{{ option.label }}</span>
				<transition name="option-check">
					<span
						v-if="modelValue === option.id"
						class="option__check"
					>
						<Checkmark16 />
					</span>
				</transition>
			</button>
		</div>

		<div class="option-picker__actions">
			<v-btn
				variant="flat"
				color="primary"
				:disabled="!modelValue"
				@click="$emit('continue')"
			>
				Continue
				<ArrowRight16 />
			</v-btn>
			<v-btn
				variant="text"
				@click="$emit('skip')"
			>
				Skip
			</v-btn>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType, type Component } from "vue";

// Carbon Icons
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";

export type OnboardingOption = {
	id: string;
	label: string;
	icon: Component;
};

export default defineComponent({
	name: "OnboardingOptionPicker",
	components: { ArrowRight16, Checkmark16 },
	props: {
		stepLabel: { type: String, default: "" },
		title: { type: String, required: true },
		subtitle: { type: String, default: "" },
		options: { type: Array as PropType<OnboardingOption[]>, default: () => [] },
		modelValue: { type: String as PropType<string | null>, default: null },
		columns: { type: Number, default: 2 },
	},
	emits: {
		"update:modelValue": (id: string) => typeof id === "string",
		continue: () => true,
		skip: () => true,
	},
});
</script>

<style lang="scss" scoped>
.option-picker {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-24);
}

.option-picker__header {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.option-picker__step {
	font-family: var(--sgnl-font-family-mono);
	font-size: var(--sgnl-font-size-caption);
	letter-spacing: var(--sgnl-font-letter-spacing-055);
	text-transform: uppercase;
	color: var(--sgnl-default-tertiary-text);
}

.option-picker__grid {
	display: grid;
	grid-template-columns: repeat(var(--option-columns, 2), 1fr);
	gap: var(--sgnl-space-10);
}

.option {
	position: relative;
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
	background: var(--sgnl-default-primary-background);
	cursor: pointer;
	text-align: left;
	transition:
		border-color var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard),
		background var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard),
		box-shadow var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard),
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
}

.option:hover {
	border-color: var(--sgnl-default-border-hover);
	background: var(--sgnl-default-secondary-background-mono);
	box-shadow: 0 0 0 var(--sgnl-space-2) var(--sgnl-focus-ring-secondary);
}

.option:hover .option__label {
	text-decoration: underline;
}

.option:active {
	transform: scale(0.98);
}

.option--selected,
.option--selected:hover {
	border-color: var(--sgnl-brand-border);
	background: var(--sgnl-brand-background-inverse);
	box-shadow: 0 0 0 var(--sgnl-space-2) var(--sgnl-focus-ring-primary);
}

.option__icon {
	flex-shrink: 0;
	width: var(--sgnl-unit-40);
	height: var(--sgnl-unit-40);
	border-radius: var(--sgnl-border-radius-6);
	background: var(--sgnl-default-secondary-background-mono);
	color: var(--sgnl-default-secondary-icon);
	display: grid;
	place-items: center;
}

.option__check {
	position: absolute;
	top: var(--sgnl-space-8);
	right: var(--sgnl-space-8);
	color: var(--sgnl-brand-icon);
	display: grid;
	place-items: center;
}

.option-check-enter-active {
	transition:
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-expressive-effect-entrance),
		opacity var(--sgnl-motion-duration-fast-02) var(--sgnl-expressive-effect-entrance);
}

.option-check-leave-active {
	transition:
		transform var(--sgnl-motion-duration-fast-01) var(--sgnl-productive-effect-exit),
		opacity var(--sgnl-motion-duration-fast-01) var(--sgnl-productive-effect-exit);
}

.option-check-enter-from,
.option-check-leave-to {
	opacity: 0;
	transform: scale(0.6);
}

.option-picker__actions {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-16);
}

.option-picker__actions :deep(.v-btn--variant-text) {
	display: inline-flex;
	justify-content: center;
}

@media (max-width: 719px) {
	.option-picker__grid {
		grid-template-columns: 1fr;
	}

	.option-picker__actions {
		flex-direction: column;
		align-items: stretch;
	}
}

@media (prefers-reduced-motion: reduce) {
	.option:active {
		transform: none;
	}

	.option-check-enter-from,
	.option-check-leave-to {
		transform: none;
	}
}
</style>
